<template>
  <v-dialog :value="value" @input="$emit('input', false)" fullscreen>
    <v-card dark rounded="xl">
      <v-sheet
        height="180px"
        :style="{ background: `linear-gradient(to right, var(--v-primary-base), #1de9b6)` }"
        class="d-flex justify-center align-center pt-4">
        <ChatGroupPicture
          v-if="group"
          :group-id="group._id"
          :initial-picture="group.bild"
          :width="125"
          :height="125"
          @updatedPicture="handlePictureUpdate" />
      </v-sheet>

      <v-overlay :value="isSaving" absolute>
        <v-progress-circular indeterminate size="64"></v-progress-circular>
      </v-overlay>

      <v-card-text class="pa-6 pt-6">
        <v-form ref="form" v-model="isValid">
          <v-text-field v-model="editableName" label="Gruppenname" :rules="[(v) => !!v || 'Name ist Pflicht']" filled rounded dense></v-text-field>

          <v-textarea v-model="editableDescription" label="Beschreibung (optional)" filled rounded dense rows="2" no-resize class="mt-4"></v-textarea>
        </v-form>

        <v-divider class="my-4"></v-divider>
        <div class="px-1">
          <user-search
            v-model="userToAdd"
            label="Neues Mitglied hinzufügen"
            :returnUser="true"
            :rights="'stats_transactions'"
            :ignoreRights="true"
            @update="handleAddUser" />
        </div>
        <v-subheader class="pl-0">Mitglieder ({{ group.members.length }})</v-subheader>
        <v-overlay :value="isBusyKicking" absolute opacity="0.5">
          <v-progress-circular indeterminate size="32"></v-progress-circular>
        </v-overlay>
        <v-list dense color="transparent" style="max-height: 200px; overflow-y: auto">
          <v-list-item
            @click="viewUserProfile(member.userId)"
            v-for="member in group.members"
            :key="member.userId._id"
            class="mb-1"
            color="grey darken-3"
            rounded="lg">
            <v-list-item-avatar size="36">
              <v-img v-if="member.userId.bild" :src="'/img/profilbilder/' + member.userId.bild"></v-img>
              <span v-else>{{ getInitials(member.userId) }}</span>
            </v-list-item-avatar>
            <v-list-item-content>
              <v-list-item-title>{{ member.userId.vorname }} {{ member.userId.nachname }}</v-list-item-title>

              <v-list-item-subtitle
                :class="{ 'primary--text text--lighten-2': getPresence(member).isOnline || (user && member.userId._id === user._id) }">
                {{ getMemberStatusText(member) }}
              </v-list-item-subtitle>
            </v-list-item-content>

            <v-list-item-action v-if="isGroupAdmin(group)">
              <v-menu offset-y left>
                <template v-slot:activator="{ on, attrs }">
                  <v-btn icon v-bind="attrs" v-on="on">
                    <v-icon>mdi-dots-vertical</v-icon>
                  </v-btn>
                </template>
                <v-list>
                  <v-list-item @click="kickMember(member)">
                    <v-list-item-icon><v-icon color="red">mdi-account-remove</v-icon></v-list-item-icon>
                    <v-list-item-title>Entfernen</v-list-item-title>
                  </v-list-item>
                  <v-list-item @click="toggleAdmin(member)">
                    <v-list-item-icon><v-icon>mdi-shield-check</v-icon></v-list-item-icon>
                    <v-list-item-title>{{ member.role === "admin" ? "Als Mitglied festlegen" : "Zum Admin machen" }}</v-list-item-title>
                  </v-list-item>
                </v-list>
              </v-menu>
            </v-list-item-action>
          </v-list-item>
        </v-list>
        <v-divider class="my-4"></v-divider>
        <v-list dense color="transparent">
          <v-list-item @click="showLeaveWarning" class="mb-1" color="red darken-3" rounded="lg" style="cursor: pointer">
            <v-list-item-icon>
              <v-icon color="red lighten-1">mdi-exit-run</v-icon>
            </v-list-item-icon>
            <v-list-item-content>
              <v-list-item-title class="red--text text--lighten-1"> Gruppe verlassen </v-list-item-title>
            </v-list-item-content>
          </v-list-item>
        </v-list>
      </v-card-text>

      <v-card-actions class="pa-6 pt-0">
        <v-btn text @click="$emit('input', false)" :disabled="isSaving"> Schließen </v-btn>
        <v-spacer></v-spacer>
        <v-btn color="primary" rounded depressed large @click="saveChanges" :loading="isSaving" :disabled="!isValid"> Speichern </v-btn>
      </v-card-actions>
    </v-card>
    <v-dialog v-model="kickConfirmDialog" max-width="400" persistent>
      <v-card dark>
        <v-card-title class="headline"> Wirklich entfernen? </v-card-title>
        <v-card-text v-if="memberToKick">
          Bist du sicher, dass du
          <strong>{{ memberToKick.userId.vorname }} {{ memberToKick.userId.nachname }}</strong>
          aus der Gruppe entfernen möchtest?
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn text @click="kickConfirmDialog = false" :disabled="isBusyKicking"> Abbrechen </v-btn>
          <v-btn color="red" text @click="confirmKick" :loading="isBusyKicking"> Ja, entfernen </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
    <v-dialog v-model="leaveConfirmDialog" max-width="400" persistent>
      <v-card dark>
        <v-card-title class="headline"> Gruppe wirklich verlassen? </v-card-title>
        <v-card-text v-if="group">
          Bist du sicher, dass du <strong>{{ group.name }}</strong> verlassen möchtest? Du kannst nicht selbst wieder beitreten, sondern musst erneut
          eingeladen werden.
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn text @click="leaveConfirmDialog = false" :disabled="isLeaving"> Abbrechen </v-btn>
          <v-btn color="red" text @click="confirmLeave" :loading="isLeaving"> Ja, verlassen </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-dialog>
</template>

<script>
import api from "@/api"
import { mapState, mapActions, mapGetters } from "vuex"
import ChatGroupPicture from "./chatGroupPicture.vue"
import userSearch from "@/components/userSearch"

export default {
  name: "ChatGroupSettingsDialog",
  components: { ChatGroupPicture, userSearch },

  props: {
    value: Boolean, // v-model
    group: Object, // Die 'activeGroupData'
  },

  data() {
    return {
      isSaving: false,
      isValid: false,
      editableName: "",
      editableDescription: "",
      editableBild: "",
      kickConfirmDialog: false,
      memberToKick: null,
      isBusyKicking: false,
      userToAdd: null,
      leaveConfirmDialog: false, // Eigener Dialog für's Verlassen
      isLeaving: false,
    }
  },

  watch: {
    // Wenn der Dialog geöffnet wird, fülle die Felder
    value: {
      handler(isOpening) {
        if (isOpening && this.group) {
          this.editableName = this.group.name
          this.editableDescription = this.group.description || ""
          this.editableBild = this.group.bild || null
        }
      },
      immediate: true,
    },
  },

  computed: {
    ...mapState("userStore", ["user"]),
    ...mapGetters("chatStore", ["getPresenceByUserId"]),
  },

  methods: {
    ...mapActions("chatStore", {
      saveGroupDetails: "saveGroupDetails",
      kickGroupMember: "kickGroupMember",
      updateGroupMemberRole: "updateGroupMemberRole",
      addMembersToGroup: "addMembersToGroup",
      viewUserProfile: "viewUserProfile",
      leaveGroup: "leaveGroup",
    }),
    showLeaveWarning() {
      // (Wir schließen den Kick-Dialog, falls er offen war)
      this.kickConfirmDialog = false
      this.leaveConfirmDialog = true
    },

    /**
     * Wird vom "Ja, verlassen"-Button aufgerufen
     */
    async confirmLeave() {
      this.isLeaving = true
      try {
        await this.leaveGroup(this.group._id)

        // Erfolg!
        this.$emit("input", false) // Schließt den Settings-Dialog
        this.leaveConfirmDialog = false
      } catch (err) {
        this.$toast.error(err.response.data.msg || "Fehler beim Verlassen")
      } finally {
        this.isLeaving = false
      }
    },
    async handleAddUser(userObject) {
      if (!userObject) return // Nur ausführen, wenn ein User ausgewählt wurde

      const isAlreadyMember = this.group.members.some((m) => m.userId._id === userObject._id)

      if (isAlreadyMember) {
        this.$nextTick(() => {
          this.userToAdd = null
        })
        return
      }

      this.isBusyKicking = true

      try {
        await this.addMembersToGroup({
          groupId: this.group._id,
          memberIds: [userObject._id], // API erwartet ein Array
        })
      } catch (err) {
        console.error("Fehler beim Hinzufügen von Mitgliedern:", err)
        // this.$toast.error("Fehler beim Hinzufügen.");
      } finally {
        this.isBusyKicking = false
        // 4. Suchfeld zurücksetzen
        this.$nextTick(() => {
          this.userToAdd = null
        })
      }
    },
    getInitials(user) {
      if (!user) return "?"
      const v = user.vorname || ""
      const n = user.nachname || ""
      return `${v.charAt(0).toUpperCase()}${n.charAt(0).toUpperCase()}`
    },

    // 4. NEUE Helfer-Methoden für den Status
    /**
     * Holt das Live-Presence-Objekt für ein Mitglied
     */
    getPresence(member) {
      if (!member || !member.userId) return { isOnline: false }
      // Nutzt den Getter, den wir gerade gemappt haben
      return this.getPresenceByUserId(member.userId._id) || { isOnline: false }
    },
    getMemberStatusText(member) {
      // 1. "Ich" - Check (Der war schon korrekt)
      if (this.user && member.userId._id === this.user._id) {
        const myRole = member.role === "admin" ? "Admin" : "Mitglied"
        return `Ich - ${myRole}`
      }

      // --- HIER IST DER FIX ---
      // Wir formatieren die Rolle IMMER, egal was sie ist.
      let roleString = ""
      if (member.role === "admin") {
        roleString = " - Admin"
      } else if (member.role === "member") {
        roleString = " - Mitglied"
      }
      // --- ENDE FIX ---

      // 2. "Status" - Check
      const presence = this.getPresence(member)
      if (presence.isOnline) {
        return `Online${roleString}` // z.B. "Online - Admin"
      }
      if (presence.lastSeen) {
        const date = new Date(presence.lastSeen)
        return `Zuletzt online: ${date.toLocaleDateString("de-CH")} ${date.toLocaleTimeString("de-CH")}${roleString}`
      }
      // 3. Fallback
      return `Offline${roleString}`
    },
    // Prüft, ob der *eingeloggte User* Admin der *angezeigten Gruppe* ist
    isGroupAdmin(group) {
      if (!this.user || !group) return false
      return group.members.some((m) => m.userId._id === this.user._id && m.role === "admin")
    },

    // Wird vom Cropper-Event aufgerufen
    handlePictureUpdate(newBildUrl) {
      this.editableBild = newBildUrl
      // Wir speichern das Bild sofort (oder warten auf "Speichern"-Button)
      this.saveChanges(true) // 'true' = nur Bild speichern
    },

    async saveChanges(onlyImage = false) {
      if (!onlyImage && !this.$refs.form.validate()) return

      this.isSaving = true
      try {
        const payload = {
          name: this.editableName,
          description: this.editableDescription,
          bild: this.editableBild, // Hält den neuen Bild-Pfad (vom Cropper)
        }

        // 1. API-Aufruf über die Store-Action
        await this.saveGroupDetails({
          groupId: this.group._id,
          updateData: payload,
        })

        // 2. Event an Parent senden (damit App.vue das Update mitkriegt)
        this.$emit("group-updated", payload)

        if (!onlyImage) {
          this.$emit("input", false) // Dialog schließen
        }
        // TODO: this.$toast.success("Gespeichert!")
      } catch (err) {
        console.error("Speichern fehlgeschlagen", err)
        // TODO: this.$toast.error("Speichern fehlgeschlagen")
      } finally {
        this.isSaving = false
      }
    },
    kickMember(member) {
      // Verhindern, dass man sich selbst kickt (doppelte Sicherheit)
      console.log("Kicking member:", member, this.user)
      if (member.userId._id === this.user._id) {
        // this.$toast.error("Du kannst dich nicht selbst entfernen.");
        return
      }
      this.memberToKick = member
      this.kickConfirmDialog = true
    },

    /**
     * Diese Methode wird vom "Ja, entfernen"-Button im Dialog aufgerufen
     */
    async confirmKick() {
      if (!this.memberToKick) return

      this.isBusyKicking = true
      try {
        await this.kickGroupMember({
          groupId: this.group._id,
          userIdToRemove: this.memberToKick.userId._id,
        })
        // this.$toast.success(`${this.memberToKick.userId.vorname} wurde entfernt.`);
      } catch (err) {
        // this.$toast.error("Fehler beim Entfernen.");
      } finally {
        this.isBusyKicking = false
        this.kickConfirmDialog = false
        this.memberToKick = null
      }
    },

    /**
     * Schaltet die Admin-Rolle eines Mitglieds um
     */
    async toggleAdmin(member) {
      // Verhindern, dass man sich selbst runterstuft (könnte gefährlich sein)
      if (member.userId._id === this.user._id) {
        // this.$toast.info("Du kannst deine eigene Rolle nicht ändern.");
        return
      }

      this.isBusyKicking = true // Wir nutzen denselben Lade-Spinner
      const newRole = member.role === "admin" ? "member" : "admin"

      try {
        await this.updateGroupMemberRole({
          groupId: this.group._id,
          userIdToUpdate: member.userId._id,
          newRole: newRole,
        })
        // this.$toast.success(`${member.userId.vorname} ist jetzt ${newRole}.`);
      } catch (err) {
        // this.$toast.error("Fehler beim Ändern der Rolle.");
      } finally {
        this.isBusyKicking = false
      }
    },
  },
}
</script>
